/**
 * Week arithmetic for the P&L's Week scope.
 *
 * A week is Monday through Sunday, named by any date inside it: the picker
 * hands over whatever day was tapped and everything downstream asks this
 * module which week that was. Pure string-in/string-out on ISO 'YYYY-MM-DD',
 * with the maths done in UTC — a 'YYYY-MM-DD' handed to `new Date()` parses
 * as UTC midnight, so reading it back with the local getters would shift the
 * date west of Greenwich (the same trap scope.ts's MONTH_NAMES comment
 * documents for months).
 *
 * Everything here is PURE and, per the rule in ./numbers.ts, never throws:
 * a malformed date returns null rather than a crash.
 */

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

function utcOf(date: string): number | null {
  if (!ISO_DAY.test(date)) return null;
  const [y, m, d] = date.split('-').map(Number);
  const ms = Date.UTC(y, m - 1, d);
  // Reject a date that only parses by rolling over (2026-02-31 -> March 3).
  const roundTrip = new Date(ms);
  if (roundTrip.getUTCFullYear() !== y || roundTrip.getUTCMonth() !== m - 1 || roundTrip.getUTCDate() !== d) {
    return null;
  }
  return ms;
}

function isoOf(ms: number): string {
  const date = new Date(ms);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** The Monday of the week containing `date`, or null when `date` is malformed. */
export function weekStart(date: string): string | null {
  const ms = utcOf(date);
  if (ms === null) return null;
  // getUTCDay: Sunday 0 .. Saturday 6. Monday-start weeks put Sunday at 6.
  const sinceMonday = (new Date(ms).getUTCDay() + 6) % 7;
  return isoOf(ms - sinceMonday * DAY_MS);
}

/** The Sunday of the week containing `date`, or null when `date` is malformed. */
export function weekEnd(date: string): string | null {
  const start = weekStart(date);
  if (start === null) return null;
  return isoOf((utcOf(start) as number) + 6 * DAY_MS);
}

/** Both ends of the week containing `date`, inclusive, or null when malformed. */
export function weekRange(date: string): { start: string; end: string } | null {
  const start = weekStart(date);
  const end = weekEnd(date);
  if (start === null || end === null) return null;
  return { start, end };
}
